// The one DIBELS period calendar, shared by client, Convex and fluency-model
// code. Boundaries are fixed day-of-year (1 Jan = 0) for every school,
// deliberately not DIBELS 8's months-from-each-school's-start rule, so a
// district starting mid-August is not shifted. In a leap year Beginning and
// the second Middle start a day earlier (5 Jul, 12 Nov); left as is.
// Research-critical: packages/fluency-model/tests/dibels-periods-golden.json
// pins them for both this module and fluency_model/awrf.py.
// dayOfYear comes from local calendar components, never ms arithmetic, so DST
// cannot shift it; the research data needs no sub-day precision.

export type DibelsPeriod = "Beginning" | "Middle" | "End";

export interface CalendarDate {
  year: number;
  month: number;
  day: number;
}

export const END_START_DAY = 50; // 20 Feb
export const BEGINNING_START_DAY = 186; // 6 Jul
export const MIDDLE_START_DAY = 316; // 13 Nov

export const DIBELS_PERIOD_SUFFIX: Readonly<
  Record<DibelsPeriod, "1" | "2" | "3">
> = Object.freeze({
  Beginning: "1",
  Middle: "2",
  End: "3",
});

const DAYS_BEFORE_MONTH_COMMON_YEAR = [
  0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334,
] as const;
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function daysInMonth(year: number, month: number): number {
  const days = DAYS_IN_MONTH[month - 1] ?? 0;
  return month === 2 && isLeapYear(year) ? days + 1 : days;
}

export function dayOfYearForCalendarDate({
  year,
  month,
  day,
}: CalendarDate): number {
  const before = DAYS_BEFORE_MONTH_COMMON_YEAR[month - 1] ?? 0;
  const leapDay = month > 2 && isLeapYear(year) ? 1 : 0;
  return before + leapDay + day - 1;
}

export function localCalendarDate(date: Date = new Date()): CalendarDate {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
}

export function dayOfYearLocal(date: Date): number {
  return dayOfYearForCalendarDate(localCalendarDate(date));
}

export function formatIsoCalendarDate({
  year,
  month,
  day,
}: CalendarDate): string {
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${String(year).padStart(4, "0")}-${mm}-${dd}`;
}

// Sent by the aide client: server code runs in UTC, and the period must follow
// the aide's local date.
export function getLocalIsoDate(date: Date = new Date()): string {
  return formatIsoCalendarDate(localCalendarDate(date));
}

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parseIsoCalendarDate(value: string): CalendarDate | null {
  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

function epochDay({ year, month, day }: CalendarDate): number {
  return Math.round(Date.UTC(year, month - 1, day) / 86_400_000);
}

// Every time zone's local date is within one day of UTC, so a client date
// further from the server's is a bad clock or tampering and is ignored.
export function resolveClientCalendarDate(
  localDate: string | undefined,
  now: Date = new Date(),
): { date: CalendarDate; source: "client" | "server" } {
  const serverDate = localCalendarDate(now);
  if (localDate === undefined) return { date: serverDate, source: "server" };
  const parsed = parseIsoCalendarDate(localDate);
  if (!parsed || Math.abs(epochDay(parsed) - epochDay(serverDate)) > 1) {
    return { date: serverDate, source: "server" };
  }
  return { date: parsed, source: "client" };
}

export function getDibelsPeriodForDay(dayOfYear: number): DibelsPeriod {
  if (dayOfYear < END_START_DAY) return "Middle";
  if (dayOfYear < BEGINNING_START_DAY) return "End";
  if (dayOfYear < MIDDLE_START_DAY) return "Beginning";
  return "Middle";
}

export function getDibelsPeriodForCalendarDate(
  date: CalendarDate,
): DibelsPeriod {
  return getDibelsPeriodForDay(dayOfYearForCalendarDate(date));
}

export function getDibelsPeriod(date: Date = new Date()): DibelsPeriod {
  return getDibelsPeriodForCalendarDate(localCalendarDate(date));
}

export function getDibelsPeriodForClientDate(
  localDate: string | undefined,
  now: Date = new Date(),
): { period: DibelsPeriod; source: "client" | "server" } {
  const { date, source } = resolveClientCalendarDate(localDate, now);
  return { period: getDibelsPeriodForCalendarDate(date), source };
}

export function getDibelsGradePeriod(
  grade: number, // 0 = K
  date: Date = new Date(),
): string {
  return gradePeriodOf(grade, getDibelsPeriod(date));
}

/** A grade as a number, a numeral, or "K", which is grade 0. */
export type DibelsGrade = number | "K" | `${number}`;

export function dibelsGradeNumber(grade: DibelsGrade): number {
  return grade === "K" ? 0 : Number(grade);
}

/** The `grade.period` key the norms tables use: "0.1" for K beginning of
 *  year, never "K.1". */
export function gradePeriodOf(
  grade: DibelsGrade,
  period: DibelsPeriod,
): string {
  return `${dibelsGradeNumber(grade)}.${DIBELS_PERIOD_SUFFIX[period]}`;
}
